import {
  Box,
  Button,
  Flex,
  Image,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoHeart } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveDisplayName, resolveProfileImageUrl } from "../util/profileImage";

const BRAND_GRADIENT = "linear(to-r, brand.400, brand.600)";
const BRAND_GRADIENT_HOVER = "linear(to-r, brand.500, brand.600)";
const MODAL_Z = 3000;

/** Fixed confetti — brand green / yellow tones */
const CONFETTI = [
  { top: "8%", left: "6%", w: "14px", h: "6px", bg: "#6EE7B7", rotate: -24 },
  { top: "12%", left: "22%", w: "10px", h: "5px", bg: "#34D399", rotate: 18 },
  { top: "6%", right: "18%", w: "12px", h: "5px", bg: "#FDE047", rotate: 32 },
  { top: "14%", right: "8%", w: "16px", h: "6px", bg: "#4ADE80", rotate: -12 },
  { top: "22%", left: "4%", w: "8px", h: "4px", bg: "#A7F3D0", rotate: 45 },
  { top: "28%", right: "4%", w: "11px", h: "5px", bg: "#FACC15", rotate: -38 },
  { top: "38%", left: "10%", w: "9px", h: "4px", bg: "#86EFAC", rotate: 12 },
  { top: "42%", right: "12%", w: "13px", h: "5px", bg: "#22C55E", rotate: -28 },
  { bottom: "38%", left: "8%", w: "12px", h: "5px", bg: "#BBF7D0", rotate: 22 },
  { bottom: "34%", right: "6%", w: "10px", h: "4px", bg: "#FDE68A", rotate: -18 },
  { bottom: "28%", left: "18%", w: "14px", h: "6px", bg: "#34D399", rotate: 36 },
  { bottom: "24%", right: "20%", w: "8px", h: "4px", bg: "#FEF08A", rotate: -8 },
  { bottom: "18%", left: "5%", w: "11px", h: "5px", bg: "#10B981", rotate: 14 },
  { bottom: "14%", right: "10%", w: "15px", h: "6px", bg: "#A3E635", rotate: -32 },
  { top: "52%", left: "3%", w: "7px", h: "3px", bg: "#D9F99D", rotate: 50 },
  { top: "48%", right: "3%", w: "9px", h: "4px", bg: "#6EE7B7", rotate: -42 },
];

function SquircleAvatar({ src, alt, offsetX = 0, zIndex = 1 }) {
  return (
    <Box
      position="relative"
      zIndex={zIndex}
      flexShrink={0}
      transform={`translateX(${offsetX}px)`}
      w={{ base: "104px", sm: "112px" }}
      h={{ base: "104px", sm: "112px" }}
      borderRadius="32px"
      overflow="hidden"
      bg="gray.100"
      boxShadow="0 12px 32px rgba(15, 23, 42, 0.14)"
      border="3px solid white"
    >
      <Image src={src} alt={alt} w="100%" h="100%" objectFit="cover" />
    </Box>
  );
}

const MatchCelebrationModal = ({
  isOpen,
  onClose,
  matchedUser,
  conversationId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "surface.800");
  const headingColor = useColorModeValue("gray.900", "white");
  const nameColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const labelColor = useColorModeValue("gray.400", "whiteAlpha.500");
  const cardShadow = useColorModeValue(
    "0 24px 64px rgba(15, 23, 42, 0.18)",
    "0 24px 64px rgba(0, 0, 0, 0.55)"
  );

  if (!isOpen) return null;

  let currentUser = null;
  try {
    currentUser = JSON.parse(localStorage.getItem("user"));
  } catch {
    currentUser = null;
  }

  const currentUserPhoto = resolveProfileImageUrl(currentUser);
  const matchedUserPhoto = resolveProfileImageUrl(matchedUser);
  const matchedName = resolveDisplayName(matchedUser, t("common.unknown"));

  const handleSayHello = () => {
    onClose();
    if (conversationId) {
      navigate(`/messages/${conversationId}`);
      return;
    }
    navigate("/Messages");
  };

  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        zIndex={MODAL_Z}
        bg="rgba(15, 23, 42, 0.55)"
        backdropFilter="blur(8px)"
        onClick={onClose}
        aria-hidden
      />
      <Flex
        position="fixed"
        inset={0}
        zIndex={MODAL_Z + 1}
        align="center"
        justify="center"
        px={4}
        py={8}
        pointerEvents="none"
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="match-celebration-title"
          pointerEvents="auto"
          w="full"
          maxW="400px"
          bg={cardBg}
          borderRadius="28px"
          boxShadow={cardShadow}
          overflow="hidden"
          position="relative"
          onClick={(e) => e.stopPropagation()}
        >
          {CONFETTI.map((piece, i) => (
            <Box
              key={i}
              position="absolute"
              top={piece.top}
              left={piece.left}
              right={piece.right}
              w={piece.w}
              h={piece.h}
              bg={piece.bg}
              borderRadius="1px"
              transform={`rotate(${piece.rotate}deg)`}
              pointerEvents="none"
              zIndex={0}
            />
          ))}

          <Box position="relative" zIndex={1} px={6} pt={8} pb={8}>
            <Text
              textAlign="center"
              fontSize="11px"
              fontWeight="semibold"
              letterSpacing="0.2em"
              textTransform="uppercase"
              color={labelColor}
              mb={4}
            >
              {t("matchModal.label")}
            </Text>

            <Text
              id="match-celebration-title"
              textAlign="center"
              fontSize={{ base: "1.55rem", sm: "1.75rem" }}
              fontWeight="900"
              lineHeight="1.05"
              letterSpacing="-0.03em"
              textTransform="uppercase"
              color={headingColor}
              mb={8}
              px={1}
            >
              {t("matchModal.headlineLine1")}
              <br />
              {t("matchModal.headlineLine2")}
            </Text>

            <Flex justify="center" align="flex-end" position="relative" minH="120px" mb={5}>
              <SquircleAvatar
                src={currentUserPhoto}
                alt={t("matchModal.yourPhoto")}
                offsetX={14}
                zIndex={1}
              />
              <SquircleAvatar
                src={matchedUserPhoto}
                alt={matchedName}
                offsetX={-14}
                zIndex={2}
              />
              <Flex
                position="absolute"
                bottom="-4px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={3}
                align="center"
                justify="center"
                w="44px"
                h="44px"
                borderRadius="full"
                bgGradient={BRAND_GRADIENT}
                border="3px solid white"
                boxShadow="0 8px 24px rgba(52, 211, 153, 0.45), 0 4px 12px rgba(253, 224, 71, 0.2)"
              >
                <Box as={IoHeart} color="white" w="22px" h="22px" />
              </Flex>
            </Flex>

            <Text
              textAlign="center"
              fontSize="md"
              fontWeight="medium"
              color={nameColor}
              mb={6}
            >
              {t("matchModal.nameAndYou", { name: matchedName })}
            </Text>

            <Button
              w="full"
              h="56px"
              borderRadius="full"
              bgGradient={BRAND_GRADIENT}
              color="gray.900"
              fontSize="md"
              fontWeight="bold"
              leftIcon={
                <Text as="span" fontSize="lg" lineHeight={1} aria-hidden>
                  👋
                </Text>
              }
              _hover={{
                bgGradient: BRAND_GRADIENT_HOVER,
                transform: "translateY(-1px)",
                boxShadow: "0 12px 32px rgba(52, 211, 153, 0.35), 0 6px 16px rgba(253, 224, 71, 0.15)",
              }}
              _active={{ transform: "scale(0.98)" }}
              boxShadow="0 10px 28px rgba(52, 211, 153, 0.32), 0 4px 14px rgba(253, 224, 71, 0.12)"
              onClick={handleSayHello}
            >
              {t("matchModal.sayHello")}
            </Button>
          </Box>
        </Box>
      </Flex>
    </Portal>
  );
};

export default MatchCelebrationModal;
